import React, { useMemo } from 'react';
import Icon from 'components/AppIcon';

export default function DiscountCalibrationPanel({ sales, categories, loading }) {
  const discountData = useMemo(() => {
    // Build actual discount per category from sales
    const actualGroups = {};
    (sales || [])?.forEach(s => {
      const cat = s?.category_name;
      if (!cat) return;
      if (!actualGroups?.[cat]) actualGroups[cat] = { category: cat, actualDiscounts: [], saleCount: 0 };
      // Actual discount = (sale_price - benchmark) / benchmark * 100 — approximate from purchase_value/weight vs sale_price
      const entryPrice = parseFloat(s?.purchase_value) / Math.max(parseFloat(s?.weight_t), 0.001);
      const salePrice = parseFloat(s?.sale_price_per_ton) || 0;
      if (entryPrice > 0 && salePrice > 0) {
        const discountPct = ((entryPrice - salePrice) / entryPrice) * 100;
        actualGroups?.[cat]?.actualDiscounts?.push(discountPct);
      }
      actualGroups[cat].saleCount += 1;
    });

    // Merge with expected discounts from categories
    const catMap = {};
    (categories || [])?.forEach(c => { catMap[c?.display_name] = c; });

    return Object.values(actualGroups)
      ?.map(g => {
        const avgActual = g?.actualDiscounts?.length > 0
          ? g?.actualDiscounts?.reduce((s, v) => s + v, 0) / g?.actualDiscounts?.length
          : null;
        const catDef = catMap?.[g?.category];
        const expectedDiscount = catDef?.default_discount_value ?? null;
        const deviation = avgActual !== null && expectedDiscount !== null ? avgActual - expectedDiscount : null;
        return {
          category: g?.category,
          expectedDiscount,
          avgActual,
          deviation,
          saleCount: g?.saleCount,
        };
      })
      ?.sort((a, b) => {
        if (a?.deviation === null) return 1;
        if (b?.deviation === null) return -1;
        return Math.abs(b?.deviation) - Math.abs(a?.deviation);
      });
  }, [sales, categories]);

  return (
    <div className="bg-card border border-border rounded-xl" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Percent" size={15} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Calibración de Descuentos</h2>
        </div>
        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Descuento esperado vs real por categoría</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-muted-foreground font-medium">Categoría</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Descuento Esperado %</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Descuento Real %</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Desviación</th>
              <th className="px-4 py-2.5 text-right text-muted-foreground font-medium">Ventas</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 })?.map((_, i) => (
                <tr key={i} className="border-b border-border/50">
                  {[1,2,3,4,5]?.map(j => (
                    <td key={j} className="px-4 py-2.5"><div className="h-3.5 bg-muted rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : discountData?.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="Percent" size={24} color="var(--color-muted-foreground)" strokeWidth={1.5} />
                    <p>Sin datos de descuentos</p>
                  </div>
                </td>
              </tr>
            ) : (
              discountData?.map((row, idx) => {
                const devAbs = row?.deviation !== null ? Math.abs(row?.deviation) : 0;
                const devColor = devAbs > 5 ? 'text-red-600' : devAbs > 2 ? 'text-amber-600' : 'text-emerald-600';
                const devBg = devAbs > 5 ? 'bg-red-100' : devAbs > 2 ? 'bg-amber-100' : 'bg-emerald-100';
                return (
                  <tr key={idx} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-foreground">{row?.category}</td>
                    <td className="px-4 py-2.5 font-mono text-right">
                      {row?.expectedDiscount !== null ? `${parseFloat(row?.expectedDiscount)?.toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-right">
                      {row?.avgActual !== null ? `${row?.avgActual?.toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {row?.deviation !== null ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono font-semibold ${devBg} ${devColor}`}>
                          {row?.deviation > 0 ? '+' : ''}{row?.deviation?.toFixed(1)}pp
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-right text-muted-foreground">{row?.saleCount}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
